import type { UseQueryOptions } from '@tanstack/react-query';

import { schedulingListScheduledApps } from '@crossnokaye/typescript-sdk/funcs/schedulingListScheduledApps';
import { ScheduledApp } from '@crossnokaye/typescript-sdk/models/components';
import { SDKError } from '@crossnokaye/typescript-sdk/models/errors';
import {
  ListScheduledAppsRequest,
  ListScheduledAppsResponse,
} from '@crossnokaye/typescript-sdk/models/operations';

import { AtlasClientFunction } from '@/client/types';
import { ScheduledAppKindValue } from '@/constants/scheduled-apps';
import { AtlasQueryWithOrgIdAndAgentId } from '@/queries/types';

const transformListScheduledAppsResponse = (
  response: ListScheduledAppsResponse
): ScheduledApp[] | undefined => {
  return response.scheduledApps;
};

export interface ListScheduledAppsQueryProps extends AtlasQueryWithOrgIdAndAgentId {
  client: AtlasClientFunction;
  enabled?: boolean;
  kind?: ScheduledAppKindValue;
  transform?: (response: ListScheduledAppsResponse) => ScheduledApp[] | undefined;
}

// Single source of truth for the query key shape. Mutations that need to
// invalidate scheduled-app lists import this helper instead of hand-rolling
// the tuple — keeps invalidation in lockstep with the factory.
export const listScheduledAppsQueryKey = (
  orgId: string | undefined,
  agentId: string | undefined,
  kind?: ScheduledAppKindValue
) => [orgId, agentId, 'listScheduledApps', kind ?? 'all'] as const;

export const listScheduledAppsQuery = ({
  orgId,
  agentId,
  client,
  enabled = true,
  kind,
  transform = transformListScheduledAppsResponse,
}: ListScheduledAppsQueryProps): UseQueryOptions<
  ListScheduledAppsResponse,
  SDKError,
  ScheduledApp[] | undefined
> => {
  return {
    enabled: enabled && !!orgId && !!agentId,
    queryKey: listScheduledAppsQueryKey(orgId, agentId, kind),
    queryFn: () =>
      client<ListScheduledAppsRequest, ListScheduledAppsResponse>({
        atlasFunc: schedulingListScheduledApps,
        request: { orgId: orgId!, agentId: agentId!, kind },
      }),
    select: transform,
  };
};
